// US-073 — Message history rendering.
//
// Thin adapter that maps `ThreadMessage[]` from the API into the DS
// `MessageBubble` composite. We auto-scroll on conv change + new message
// (matches `ls-inbox.jsx:471-546`).
//
// The DS already covers the visual contract for in / out / system /
// sending / sent bubbles; we only contribute:
//   - manual-reply right-alignment (US-079) — `outboundSource ===
//     'manual_linkedin'` renders on the right via `role: 'out'`.
//   - timestamp formatting via the `useFormatters().dateTime` helper.
//   - the `<ol>` landmark + auto-scroll behaviour.

import { Fragment, useEffect, useRef } from 'react';

import { useFormatters, useT } from '@barel/i18n';
import { Avatar, MessageBubble } from '@barel/ui';

import type { ThreadMessage } from './types';

/**
 * SPEC-SEND-TRANSPORT §6 — known `deliveryError` reason codes the API can
 * write (`ext/send/result`'s `SEND_ERROR_REASONS` taxonomy + the enqueue-time
 * `enqueue_failed` / stale-rescan `expired` reasons). Anything else falls
 * back to the generic `unknown` i18n copy instead of risking a missing key.
 */
const KNOWN_DELIVERY_ERROR_REASONS = new Set([
  'rate-limited',
  'thread-gone',
  'blocked-by-recipient',
  'inmail-required',
  'session-expired',
  'enqueue_failed',
  'expired',
  'unknown',
]);

function deliveryErrorReasonKey(reason: string | null | undefined): string {
  return reason && KNOWN_DELIVERY_ERROR_REASONS.has(reason) ? reason : 'unknown';
}

export interface MessageBubblesProps {
  conversationId: string;
  contactInitials: string;
  messages: ThreadMessage[];
  /** Append-only marker — when last id changes, scroll bumps to bottom. */
  scrollKey?: string;
}

export function MessageBubbles({
  conversationId,
  contactInitials,
  messages,
  scrollKey,
}: MessageBubblesProps) {
  const formatters = useFormatters();
  const t = useT('inbox');
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Auto-scroll on conv change AND on new messages.
  useEffect(() => {
    const node = scrollRef.current;
    if (!node) return;
    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    try {
      node.scrollTo({
        top: node.scrollHeight,
        behavior: reduced ? 'auto' : 'smooth',
      });
    } catch {
      node.scrollTop = node.scrollHeight;
    }
  }, [conversationId, scrollKey, messages.length]);

  return (
    <div
      ref={scrollRef}
      data-ls-region="messages-scroll"
      style={{
        flex: '1 1 auto',
        minHeight: 0,
        overflowY: 'auto',
        padding: 'var(--space-4)',
      }}
    >
      <ol
        aria-label="conversation messages"
        style={{
          listStyle: 'none',
          margin: 0,
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-3)',
        }}
      >
        {messages.map((m) => {
          const stamp = pickTimestamp(m);
          const tsLabel = stamp
            ? `${formatters.formatDate(new Date(stamp))} ${formatters.formatTime(new Date(stamp))}`
            : undefined;
          if (m.role === 'in') {
            // The DS `MessageBubble` renders an `<li>`; we co-locate the
            // contact avatar via an outer flex group rendered as an `<li>`
            // wrapping a `<span>` (so we don't nest `<li>`s — invalid HTML).
            // The bubble inside renders as a span/div by switching off
            // its outer LI via a custom data attribute is unavailable, so
            // we drop the avatar to its own visible element next to the
            // bubble using `position: relative` flex.
            return (
              <li
                key={m.id}
                data-ls-component="message-row"
                data-role="in"
                style={{
                  listStyle: 'none',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: 'var(--space-1)',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    gap: 'var(--space-2)',
                  }}
                >
                  <Avatar size="sm" initials={contactInitials} tone="neutral" alt="" aria-hidden />
                  <span
                    data-role="in"
                    style={{
                      background: 'var(--bg-surface)',
                      color: 'var(--text-primary)',
                      border: '1px solid var(--border-strong)',
                      borderRadius: 'var(--radius-bubble-in)',
                      padding: 'var(--space-2_5) var(--space-3)',
                      fontSize: 'var(--text-sm)',
                      lineHeight: 'var(--line-sm)',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      maxWidth: '78%',
                      display: 'inline-block',
                    }}
                  >
                    {m.body}
                  </span>
                </div>
                {tsLabel ? (
                  <span
                    style={{
                      fontSize: 'var(--text-2xs)',
                      lineHeight: 'var(--line-xs)',
                      color: 'var(--text-tertiary)',
                      marginLeft: 'calc(var(--space-7) + var(--space-2))',
                    }}
                  >
                    {tsLabel}
                  </span>
                ) : null}
              </li>
            );
          }
          if (m.role === 'system') {
            return (
              <MessageBubble
                key={m.id}
                role="system"
                text={m.body}
                detail={m.systemDetail ?? undefined}
              />
            );
          }
          // `out` — includes both AI sends and manual_linkedin reconciliation
          // (US-079). DS bubble doesn't differentiate visually; consumers can
          // surface a system message above to hint at provenance.
          //
          // SPEC-SEND-TRANSPORT §6 — delivery truth. `pending` renders the DS
          // `sending` bubble role (queued, not yet handed to LinkedIn);
          // `sent` / `null` (untracked / pre-migration rows) render the
          // normal `out` bubble; `failed` keeps the `out` bubble but adds a
          // reason line so the user knows LinkedIn never received it.
          const deliveryState = m.deliveryState ?? 'sent';
          if (deliveryState === 'failed') {
            return (
              <Fragment key={m.id}>
                <MessageBubble role="out" text={m.body} timestamp={tsLabel} />
                <li
                  data-ls-component="message-delivery-error"
                  style={{
                    listStyle: 'none',
                    display: 'flex',
                    justifyContent: 'flex-end',
                  }}
                >
                  <span
                    style={{
                      fontSize: 'var(--text-2xs)',
                      lineHeight: 'var(--line-xs)',
                      color: 'var(--error)',
                      maxWidth: '78%',
                      textAlign: 'right',
                    }}
                  >
                    {t('message.delivery.failed')}
                    {' — '}
                    {t(`message.delivery.failedReason.${deliveryErrorReasonKey(m.deliveryError)}`)}
                  </span>
                </li>
              </Fragment>
            );
          }
          return (
            <MessageBubble
              key={m.id}
              role={deliveryState === 'pending' ? 'sending' : 'out'}
              text={m.body}
              timestamp={tsLabel}
            />
          );
        })}
      </ol>
    </div>
  );
}

function pickTimestamp(m: ThreadMessage): string | null {
  return m.sentAt ?? m.receivedAt ?? m.createdAt ?? null;
}
